import { useCallback, useState } from "react";

export type RecipeAiFlow = "generate" | "photo" | "link";

type UseRevisionGatedAiFlowArgs = {
  /** True once the user has already confirmed a nutrient-affecting revision. */
  isRevision: boolean;
  /** Enter revision mode (no pending form action required). */
  onMakeRevision: () => void;
  /** Open the generate / photo / link AI UI after revision is ensured. */
  onOpenFlow: (flow: RecipeAiFlow) => void;
};

type UseRevisionGatedAiFlowResult = {
  /** Non-null while the revision dialog is shown for an AI action. */
  pendingAiFlow: RecipeAiFlow | null;
  isAwaitingRevisionForAi: boolean;
  requestAiFlow: (flow: RecipeAiFlow) => void;
  cancelPendingAiFlow: () => void;
  /** Confirm revision, then open the pending AI flow (if any). */
  confirmRevisionAndOpenAi: () => void;
};

/** Open AI immediately when already a revision; otherwise park behind the confirm dialog. */
export const resolveRevisionGatedAiRequest = (
  isRevision: boolean,
  flow: RecipeAiFlow
): { kind: "open"; flow: RecipeAiFlow } | { kind: "await-revision"; flow: RecipeAiFlow } =>
  isRevision ? { kind: "open", flow } : { kind: "await-revision", flow };

/**
 * Gates AI generate/transcribe behind a revision confirm when editing an
 * existing recipe or menu item that is not yet a revision.
 */
export const useRevisionGatedAiFlow = ({
  isRevision,
  onMakeRevision,
  onOpenFlow,
}: UseRevisionGatedAiFlowArgs): UseRevisionGatedAiFlowResult => {
  const [pendingAiFlow, setPendingAiFlow] = useState<RecipeAiFlow | null>(null);

  const requestAiFlow = useCallback(
    (flow: RecipeAiFlow) => {
      const resolved = resolveRevisionGatedAiRequest(isRevision, flow);
      if (resolved.kind === "open") {
        onOpenFlow(resolved.flow);
        return;
      }
      setPendingAiFlow(resolved.flow);
    },
    [isRevision, onOpenFlow]
  );

  const cancelPendingAiFlow = useCallback(() => {
    setPendingAiFlow(null);
  }, []);

  const confirmRevisionAndOpenAi = useCallback(() => {
    onMakeRevision();
    if (pendingAiFlow !== null) {
      const flow = pendingAiFlow;
      setPendingAiFlow(null);
      onOpenFlow(flow);
    }
  }, [onMakeRevision, onOpenFlow, pendingAiFlow]);

  return {
    pendingAiFlow,
    isAwaitingRevisionForAi: pendingAiFlow !== null,
    requestAiFlow,
    cancelPendingAiFlow,
    confirmRevisionAndOpenAi,
  };
};
